import React, { useCallback, useEffect, useRef } from "react";
import { Animate } from "react-move";

import { easeBounceOut } from "@/utils/animation";
import base64 from "@/utils/base64";
import { classNameResolver } from "@/utils/transform";

import { centerPoint } from "../MeterConstants";

export type MeterVoteHandleProps = {
  icons: IIcons;
  hasVoted: boolean;
  voteHandle: number;
  voteHandleTouched: boolean;
  onMoveVoteHandle: (clientX: number, clientY: number) => void;
  onSnapVoteHandle: (clientX: number, clientY: number) => void;
  mobile?: boolean;
};

const MeterVoteHandle: React.FC<MeterVoteHandleProps> = ({
  icons,
  mobile,
  hasVoted,
  voteHandle,
  voteHandleTouched,
  onMoveVoteHandle,
  onSnapVoteHandle,
}) => {
  const voteHandleRef = useRef<SVGGElement>(null);

  const handleStart = useCallback((e: TouchEvent) => {
    e.preventDefault();
  }, []);

  const handleMove = useCallback(
    (e: TouchEvent) => {
      e.preventDefault();
      const touch = e.touches.item(0);
      if (touch) {
        onMoveVoteHandle(touch.clientX, touch.clientY);
      }
    },
    [onMoveVoteHandle]
  );

  const handleEnd = useCallback(
    (e: TouchEvent) => {
      e.preventDefault();
      const touch = e.changedTouches.item(0);
      if (touch) {
        onSnapVoteHandle(touch.clientX, touch.clientY);
      }
    },
    [onSnapVoteHandle]
  );

  useEffect(() => {
    const element = voteHandleRef.current;
    if (!element) return;

    element.addEventListener("touchstart", handleStart, { passive: false });
    element.addEventListener("touchmove", handleMove, { passive: false });
    element.addEventListener("touchend", handleEnd, { passive: false });

    return () => {
      element.removeEventListener("touchstart", handleStart);
      element.removeEventListener("touchmove", handleMove);
      element.removeEventListener("touchend", handleEnd);
    };
  }, [handleStart, handleMove, handleEnd]);

  const sliderScale = mobile ? 1.5 : 1;
  const sliderX = -((28 / 2) * sliderScale);
  const sliderY = -((47 / 2) * sliderScale);
  const sliderWidth = 28 * sliderScale;
  const sliderHeight = 47 * sliderScale;

  return (
    <Animate
      start={{
        x: voteHandle,
      }}
      update={
        voteHandleTouched
          ? {
              x: [voteHandle],
              timing: {
                duration: 1,
              },
            }
          : {
              x: [voteHandle],
              timing: {
                delay: 1,
                duration: 300,
                ease: easeBounceOut,
              },
            }
      }
    >
      {(animation: { x: number }) => (
        <g
          transform={`translate(${animation.x}, ${centerPoint.y})`}
          style={{ cursor: "pointer" }}
          ref={voteHandleRef}
          data-qa="meter:handle"
        >
          <g
            className={classNameResolver(["meterHandle"], {
              ["meterPulse"]: !hasVoted,
              ["meterHandleTouched"]: voteHandleTouched,
            })}
          >
            <image
              xlinkHref={
                "data:image/svg+xml;base64," + base64.encode(icons.Slider)
              }
              x={sliderX}
              y={sliderY}
              width={sliderWidth}
              height={sliderHeight}
              onDragStart={(e: React.MouseEvent) => e.preventDefault()}
            />
          </g>
        </g>
      )}
    </Animate>
  );
};

export default MeterVoteHandle;
